/**
 * _TravelAwareMockupGallery — static design-pass gallery.
 *
 * MOCKUP-ONLY ROUTE.  Feb 18, 2026.
 *
 * Purpose: real-pixel emotional-tone checkpoint before any
 * implementation. Each section is a static snapshot of one of
 * the three Travel-Aware components in one of its design-locked
 * states. No API calls. No persistence. No scheduling logic.
 *
 * Operator review URL (preview only):
 *   /_mockup/travel-aware
 *
 * After review + GO/HOLD this file (and the route) will be
 * removed in the implementation sprint cleanup commit.
 */
import { useState } from "react";
import TravelBufferWarning from "../components/instructor/TravelBufferWarning";
import TravelSummaryStrip from "../components/instructor/TravelSummaryStrip";
import LiveTravelFeasibility from "../components/instructor/LiveTravelFeasibility";
import InstructorSchedulingPrefsPage from "./InstructorSchedulingPrefsPage";

function Section({ title, subtitle, children }) {
  return (
    <section className="border border-slate-200 bg-white rounded-2xl p-5 shadow-sm">
      <header className="mb-3">
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
          {subtitle}
        </p>
        <h3 className="text-base font-bold text-slate-900 mt-0.5">{title}</h3>
      </header>
      <div>{children}</div>
    </section>
  );
}

export default function TravelAwareMockupGallery() {
  const [showPrefsPage, setShowPrefsPage] = useState(false);

  return (
    <div
      data-testid="travel-aware-mockup-gallery"
      className="min-h-dvh bg-slate-100"
    >
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
          Design-only mockup · Feb 18, 2026
        </p>
        <h1 className="text-xl font-bold text-slate-900 mt-1">
          Travel-Aware Scheduling — Static Component Gallery
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Three components in their locked states. Zero behaviour
          wired. Operator emotional-tone checkpoint before
          implementation begins.
        </p>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">

        <Section
          subtitle="Component 1 · Tight gap surface"
          title="TravelBufferWarning — active state"
        >
          <TravelBufferWarning
            travelMinutes={23}
            bufferMinutes={10}
            availableMinutes={18}
            fromLocation="Wimbledon"
            toLocation="Putney"
          />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Muted amber. Calm heading. Math is shown openly.
            &ldquo;Continue anyway&rdquo; preserves instructor authority — never
            disabled. Tap it to see the override receipt state below.
          </p>
        </Section>

        <Section
          subtitle="Component 1 · Override receipt"
          title="TravelBufferWarning — overridden state"
        >
          <div className="bg-white p-4 border border-slate-200 rounded-md">
            <p className="text-[12px] text-slate-500 px-1">
              Travel buffer overridden. 18 min gap.
            </p>
          </div>
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Amber banner unmounts. Quiet slate one-liner remains so
            the instructor still sees what was decided. No surveillance
            framing.
          </p>
        </Section>

        <Section
          subtitle="Component 2 · Operational honesty"
          title="TravelSummaryStrip — collapsed (default)"
        >
          <TravelSummaryStrip
            travelMinutes={23}
            bufferMinutes={10}
            distanceMiles={5.2}
            speedMph={25}
            overheadMinutes={5}
            totalMinutes={33}
            gapDisplay="2:30"
          />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Read-only. One-line summary. Click anywhere on the row
            to expand the math.
          </p>
        </Section>

        <Section
          subtitle="Component 2 · Operational honesty"
          title="TravelSummaryStrip — expanded"
        >
          <TravelSummaryStrip
            travelMinutes={23}
            bufferMinutes={10}
            distanceMiles={5.2}
            speedMph={25}
            overheadMinutes={5}
            totalMinutes={33}
            gapDisplay="2:30"
            defaultExpanded
          />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Full inspectable math. Instructors can see EXACTLY how
            the buffer was derived. No black-box &ldquo;AI says…&rdquo; copy.
          </p>
        </Section>

        <Section
          subtitle="Component 2 · Fallback"
          title="TravelSummaryStrip — postcode missing"
        >
          <TravelSummaryStrip postcodeMissing />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Calm-baseline fallback when input data is incomplete.
            No alarm framing.
          </p>
        </Section>

        <Section
          subtitle="Phase B2 · Live wired (feature-flagged)"
          title="LiveTravelFeasibility — comfortable case"
        >
          <LiveTravelFeasibility
            fromPostcode="SW19 1AA"
            toPostcode="SW15 6AA"
            adjacentEndIso="2026-03-01T09:00:00+00:00"
            slotStartIso="2026-03-01T10:00:00+00:00"
            slotDurationMinutes={60}
          />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Live data via <code className="text-[11px] bg-slate-100 px-1 rounded">GET /api/scheduling/travel-feasibility</code>.
            Renders only when the instructor has opted in via
            <strong> Scheduling Prefs → Travel-aware preview</strong>.
            Calm-baseline silence when off.
          </p>
        </Section>

        <Section
          subtitle="Phase B2 · Live wired (feature-flagged)"
          title="LiveTravelFeasibility — impossible case"
        >
          <LiveTravelFeasibility
            fromPostcode="SW19 1AA"
            toPostcode="NE1 1AA"
            adjacentEndIso="2026-03-01T09:00:00+00:00"
            slotStartIso="2026-03-01T09:02:00+00:00"
            slotDurationMinutes={60}
          />
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Tight-gap warning appears above the math strip. Continue/
            override remains available.
          </p>
        </Section>

        <Section
          subtitle="Component 3 · Quiet preferences"
          title="InstructorSchedulingPrefsPage — full layout"
        >          {!showPrefsPage ? (
            <button
              type="button"
              onClick={() => setShowPrefsPage(true)}
              className="text-sm font-bold text-slate-700 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
              data-testid="open-prefs-mockup-btn"
            >
              View page mockup ↓
            </button>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
              <InstructorSchedulingPrefsPage />
            </div>
          )}
          <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
            Single-column preferences sheet. Generous spacing.
            Inputs save locally only — no API in this mockup.
            Saved confirmation uses muted emerald (only positive
            terminal colour permitted).
          </p>
        </Section>

      </main>

      <footer className="max-w-5xl mx-auto px-6 pb-12 pt-2 text-[11px] text-slate-400 leading-relaxed">
        Static mockup gallery — design pass only. After operator
        GO/HOLD, this file is removed in the implementation
        sprint cleanup commit.
      </footer>
    </div>
  );
}
